import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { trackErrorEvent } from "../utils/analytics";
import type { ImpersonatableUser } from "../utils/models";

export const useUserSwitcher = (enabled: boolean = false) => {
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment?.department_id;

	const {
		data: impersonatableUsers = [],
		isPending,
		error,
	} = useQuery<ImpersonatableUser[]>({
		queryKey: ["impersonatableUsers", departmentId],
		queryFn: async () => {
			if (!departmentId) {
				throw new Error("Department ID is required");
			}
			const response = await backendService.getImpersonatableUsers(departmentId);
			return response.data;
		},
		enabled: enabled && !!departmentId,
		refetchOnWindowFocus: false,
	});

	// Handle errors
	useEffect(() => {
		if (error) {
			console.error("Error fetching impersonatable users:", error);
			trackErrorEvent("error fetching impersonatable users", error, {
				action: "error fetching impersonatable users",
			});
			showSnackbar(t("error.fetching.users"), "error");
		}
	}, [error, showSnackbar, t]);

	return {
		impersonatableUsers,
		isPending,
		error,
	};
};
